import { type ReactNode, type RefObject, useLayoutEffect, useRef } from 'react';
import { useFamilyFocusMode } from './useFamilyFocusMode';

/** Native dialog provides the focus trap, inert background and mobile top layer. */
export function FamilyDialog({ title, description, busy, onClose, returnFocusRef, fallbackFocusRef, children }: {
  title: string;
  description: string;
  busy: boolean;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLButtonElement>;
  fallbackFocusRef?: RefObject<HTMLButtonElement>;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useFamilyFocusMode(ref, returnFocusRef);
  useLayoutEffect(() => {
    const dialog = ref.current!;
    const trigger = returnFocusRef.current;
    const { body, documentElement } = document;
    const previousStyles = {
      position: body.style.position, top: body.style.top, left: body.style.left,
      width: body.style.width, overflow: body.style.overflow,
    };
    const previousOverflow = documentElement.style.overflow;
    const { scrollX, scrollY } = window;
    Object.assign(body.style, {
      position: 'fixed', top: `${-scrollY}px`, left: `${-scrollX}px`, width: '100%', overflow: 'hidden',
    });
    documentElement.style.overflow = 'hidden';
    dialog.showModal();
    dialog.querySelector<HTMLElement>('[data-initial-focus]')?.focus();
    return () => {
      dialog.close();
      Object.assign(body.style, previousStyles);
      documentElement.style.overflow = previousOverflow;
      window.scrollTo(scrollX, scrollY);
      const target = trigger?.isConnected ? trigger : fallbackFocusRef?.current;
      target?.focus({ preventScroll: true });
      if (fallbackFocusRef) {
        // React may remove the last profile's toggle later in this same commit.
        queueMicrotask(() => {
          if (!trigger?.isConnected) fallbackFocusRef.current?.focus({ preventScroll: true });
        });
      }
    };
  }, [returnFocusRef, fallbackFocusRef]);

  return (
    <dialog ref={ref} className="family-dialog" aria-modal="true"
      aria-labelledby="family-dialog-title" aria-describedby="family-dialog-description"
      aria-busy={busy}
      onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}
      onClose={(event) => { if (!event.currentTarget.open && !busy) onClose(); }}>
      <button type="button" className="family-dialog__close" aria-label="Schliessen" disabled={busy} onClick={onClose}>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
      </button>
      <h2 id="family-dialog-title">{title}</h2>
      <p id="family-dialog-description" className="family-dialog__description">{description}</p>
      {children}
    </dialog>
  );
}
